import { IndustryModeEnum } from "../../../api/constants";

export const RBPayEmploymentType = {
  EMPLOYEE: "employee",
  BUSINESS: "business",
} as const;

export const RBPayPaymentMethod = {
  DIRECT_DEPOSIT: "direct-deposit",
  EMAIL_TRANSFER: "email-money-transfer",
} as const;

export const RBPayTaxProfile = {
  HIGH: "high-tax-profile",
  LOW: "low-tax-profile",
} as const;

export const RBPayBusinessType = {
  SOLE_PROPRIETOR: "sole-proprietor",
  INCORPORATED: "incorporated",
} as const;

export type RBPayEmploymentTypeValue = typeof RBPayEmploymentType[keyof typeof RBPayEmploymentType];
export type RBPayPaymentMethodValue = typeof RBPayPaymentMethod[keyof typeof RBPayPaymentMethod];
export type RBPayTaxProfileValue = typeof RBPayTaxProfile[keyof typeof RBPayTaxProfile];
export type RBPayBusinessTypeValue = typeof RBPayBusinessType[keyof typeof RBPayBusinessType];
export type RBPayIndustryMode = typeof IndustryModeEnum[keyof typeof IndustryModeEnum];

export interface RBPayDocument {
  id: string;
  name: string;
  type: string;
  size: number;
  previewUrl: string;
  uploadedAt: string;
  source: "mock-upload" | "mock-existing" | "s3";
  // Raw S3 object key for documents backed by the RB Pay API — re-sent as
  // `voidChequeS3Key` on update so an unchanged document is kept.
  s3Key?: string;
}

export interface RBPayEmployeeProfile {
  sinNumber: string;
  // "YYYY-MM-DD". Employee-only: collected for the employee payroll profile and
  // stored on pharmacists.date_of_birth by the backend.
  dateOfBirth: string;
  taxProfile: RBPayTaxProfileValue;
}

export interface RBPayBusinessProfile {
  businessType: RBPayBusinessTypeValue;
  legalBusinessName: string;
  businessNumber: string;
  certificationAccepted: boolean;
}

export interface RBPayDirectDepositSettings {
  institutionNumber: string;
  transitNumber: string;
  accountNumber: string;
  document: RBPayDocument | null;
  authorizationAccepted: boolean;
}

export interface RBPayEmailTransferSettings {
  email: string;
  certificationAccepted: boolean;
  emailVerified: boolean;
  verifiedEmail: string;
  shadowEmail: string;
}

export interface RBPayApprovalInfo {
  approved: boolean;
  status?: string | null;
}

export interface RBPayProfileDashboardState {
  enabled: boolean;
}

export interface RBPaySettingsState {
  workerId: string;
  industryMode: RBPayIndustryMode;
  workerEmail: string;
  employmentType: RBPayEmploymentTypeValue | null;
  paymentMethod: RBPayPaymentMethodValue;
  employeeProfile: RBPayEmployeeProfile;
  businessProfile: RBPayBusinessProfile;
  directDeposit: RBPayDirectDepositSettings;
  emailTransfer: RBPayEmailTransferSettings;
  approvalInfo: RBPayApprovalInfo;
  dashboard: RBPayProfileDashboardState;
  meta: {
    updatedAt: string | null;
    source: "fe-mock" | "be";
  };
}

export interface RBPayProfileSectionDraft {
  industryMode: RBPayIndustryMode;
  workerEmail: string;
  employmentType: RBPayEmploymentTypeValue | null;
  employeeProfile: RBPayEmployeeProfile;
  businessProfile: RBPayBusinessProfile;
}

export interface RBPayPaymentSectionDraft {
  workerEmail: string;
  paymentMethod: RBPayPaymentMethodValue;
  directDeposit: RBPayDirectDepositSettings;
  emailTransfer: RBPayEmailTransferSettings;
}

export interface RBPayNetworkConfiguration {
  statementsUrl: string;
  contactEmail: string;
}

export interface RBPayEmailVerificationRequest {
  requestId: string;
  email: string;
  expiresAt: string;
}

export interface RBPayVerificationState {
  open: boolean;
  email: string;
  code: string;
  errorMessage: string;
  successMessage: string;
  isRequesting: boolean;
  isVerifying: boolean;
  pendingSave: boolean;
}

export interface RBPayFieldErrors {
  employmentType?: string;
  paymentMethod?: string;
  employeeProfile?: {
    sinNumber?: string;
    dateOfBirth?: string;
    taxProfile?: string;
  };
  businessProfile?: {
    businessType?: string;
    legalBusinessName?: string;
    businessNumber?: string;
    certificationAccepted?: string;
  };
  directDeposit?: {
    institutionNumber?: string;
    transitNumber?: string;
    accountNumber?: string;
    document?: string;
    authorizationAccepted?: string;
  };
  emailTransfer?: {
    email?: string;
    certificationAccepted?: string;
    emailVerified?: string;
  };
}

export interface RBPayProfileSectionErrors {
  employmentType?: string;
  employeeProfile?: {
    sinNumber?: string;
    dateOfBirth?: string;
    taxProfile?: string;
  };
  businessProfile?: {
    businessType?: string;
    legalBusinessName?: string;
    businessNumber?: string;
    certificationAccepted?: string;
  };
}

export interface RBPayPaymentSectionErrors {
  paymentMethod?: string;
  directDeposit?: {
    institutionNumber?: string;
    transitNumber?: string;
    accountNumber?: string;
    document?: string;
    authorizationAccepted?: string;
  };
  emailTransfer?: {
    email?: string;
    certificationAccepted?: string;
    emailVerified?: string;
  };
}

export interface RBPaySummaryModel {
  approved: boolean;
  employmentTypeLabel: string;
  paymentMethodLabel: string;
  paymentValue: string;
  taxProfileLabel?: string;
  businessTypeLabel?: string;
  legalBusinessName?: string;
  businessNumber?: string;
}

export interface RBPayProfileDashboardStep {
  key: "profile" | "payment" | "approval";
  label: string;
  description: string;
  complete: boolean;
}

export interface RBPayProfileDashboardModel {
  status: "not-started" | "pending" | "on-hold" | "active";
  statusLabel: string;
  approvalLabel: string;
  progressLabel: string;
  progressDescription: string;
  progressValue: number;
  setupStarted: boolean;
  setupComplete: boolean;
  canEnable: boolean;
  isEnabled: boolean;
  toggleHelperText: string;
  steps: RBPayProfileDashboardStep[];
}
